/* eslint-disable no-console -- dev console stub logs Mixpanel calls instead of sending them */
import mixpanel, { type Config, type Dict, type Mixpanel } from 'mixpanel-browser';

import { MIXPANEL_FIRST_PARTY_API_HOST, MIXPANEL_FIRST_PARTY_API_ROUTES } from '../firstPartyHosts';
import { resolveNoddiDualTldUrl } from '../resolveNoddiDualTldUrl';
import type { EventProps, EventShipper, ShipperGroup, ShipperReplay, ShipperUser } from './types';

// First-party `signals.*` hosts (any brand zone — see resolveNoddiDualTldUrl).
// Only these get `api_routes` remapped; vendor hosts keep Mixpanel's defaults.
const SIGNALS_HOST = /^signals\.(noddi\.(co|no)|bynavio\.com|bynav\.io)$/i;

const isFirstPartySignalsHost = (apiHost: string): boolean => {
  try {
    return SIGNALS_HOST.test(new URL(apiHost).hostname);
  } catch {
    return false;
  }
};

// rrweb-based session replay (Mixpanel SDK). Off unless `enabled: true`. Mirrors
// OpenPanel's `sessionReplay` shape so both destinations are configured the same
// way. Recordings ride the same project token as events. Privacy defaults are
// safe — Mixpanel masks all inputs and (via its default `*` text selector) all
// text out of the box, so leave the masking knobs unset to keep PII (phone,
// booking, customer data) hidden; override only to reveal/hide specific nodes.
// See https://docs.mixpanel.com/docs/tracking-methods/sdks/javascript/javascript-replay
export interface MixpanelSessionReplayOptions {
  enabled: boolean;
  // Fraction of sessions auto-recorded, 0..1 (mapped to Mixpanel's
  // `record_sessions_percent`). Matches OpenPanel's `sampleRate`. Defaults to 1
  // when enabled; set 0 for manual-only recording via `startSessionRecording()`.
  sampleRate?: number;
  // CSS selector for text nodes to mask (Mixpanel masks all text by default).
  maskTextSelector?: string;
  // CSS selector for nodes to omit from the recording entirely.
  blockSelector?: string;
}

export interface MixpanelOptions {
  // Mixpanel project token. Required unless `stub` is set.
  token?: string;
  // First-party ingest host. Defaults to `https://signals.noddi.co` (dual-TLD
  // rewritten at construct time). Pass Mixpanel's own host only for non-Navio
  // consumers that are not behind the Traefik proxy.
  apiHost?: string;
  debug?: boolean;
  persistence?: 'localStorage' | 'cookie';
  // rrweb session replay. Off unless `sessionReplay.enabled` is true; the
  // recorder bundle is lazy-loaded by the SDK only when recording starts.
  sessionReplay?: MixpanelSessionReplayOptions;
  // When true, never talks to Mixpanel — logs calls to the console instead.
  // Use on local dev so you can see events without polluting the project.
  stub?: boolean;
}

// Translate our camelCase `sessionReplay` knobs into the Mixpanel SDK's
// `record_*` init config. Only emits keys the caller set so the SDK keeps its
// safe masking defaults. Returns nothing when replay is disabled.
const toRecordConfig = (replay?: MixpanelSessionReplayOptions): Partial<Config> => {
  if (!replay?.enabled) {
    return {};
  }
  return {
    record_sessions_percent: (replay.sampleRate ?? 1) * 100,
    ...(replay.maskTextSelector !== undefined && { record_mask_text_selector: replay.maskTextSelector }),
    ...(replay.blockSelector !== undefined && { record_block_selector: replay.blockSelector })
  };
};

// `mixpanel-browser` ships these at runtime but not in its `.d.ts`.
type MixpanelRuntime = Mixpanel & {
  unregister: (property: string) => void;
  get_session_replay_url: () => string | null;
};

// Console-logging stand-in matching the slice of the Mixpanel API we use.
const consoleStub = (): MixpanelRuntime =>
  ({
    track: (name: string, props?: Dict, callback?: () => void) => {
      console.log('mixpanel: track', name, props);
      callback?.();
    },
    identify: (id?: string) => console.log('mixpanel: identify', id),
    register: (props: Dict) => console.log('mixpanel: register', props),
    unregister: (key: string) => console.log('mixpanel: unregister', key),
    people: { set: (props: unknown) => console.log('mixpanel: set', props) },
    set_group: (key: string, ids: unknown) => console.log('mixpanel: set_group', key, ids),
    get_group: (key: string, id: string) => ({
      set: (props: unknown) => console.log('mixpanel: get_group.set', key, id, props)
    }),
    start_session_recording: () => console.log('mixpanel: start_session_recording'),
    stop_session_recording: () => console.log('mixpanel: stop_session_recording'),
    get_session_recording_properties: () => ({}),
    get_session_replay_url: () => null,
    reset: () => console.log('mixpanel: reset')
  }) as unknown as MixpanelRuntime;

// Mixpanel destination. Unlike Faro it retains PII (email, phone) for product
// analytics. Pass `stub: true` on local dev to log instead of send.
export class MixpanelShipper implements EventShipper {
  readonly name = 'mixpanel';
  private client: MixpanelRuntime;
  private initialized = false;

  constructor(options: MixpanelOptions) {
    if (options.stub) {
      this.client = consoleStub();
      this.initialized = true;
      return;
    }

    // The SDK default export is typed narrower than the lib it is; widen to the
    // runtime slice (one cast, see `MixpanelRuntime`).
    const lib: Mixpanel = mixpanel;
    this.client = lib as MixpanelRuntime;
    if (!options.token) {
      // No token → stay not-ready so the dispatcher skips this shipper.
      return;
    }

    const apiHost =
      resolveNoddiDualTldUrl(options.apiHost ?? MIXPANEL_FIRST_PARTY_API_HOST) ?? MIXPANEL_FIRST_PARTY_API_HOST;
    mixpanel.init(options.token, {
      api_host: apiHost,
      ...(isFirstPartySignalsHost(apiHost) ? { api_routes: { ...MIXPANEL_FIRST_PARTY_API_ROUTES } } : {}),
      debug: options.debug ?? false,
      persistence: options.persistence ?? 'localStorage',
      ...toRecordConfig(options.sessionReplay)
    });
    this.initialized = true;
  }

  isReady() {
    return this.initialized;
  }

  trackEvent(name: string, props?: EventProps, callback?: () => void) {
    this.client.track(name, props, callback);
  }

  // Mixpanel has no metric channel — record as a valued event so the number is
  // analyzable (avg/percentiles) alongside the rest of the funnel.
  measure(name: string, value: number, props?: EventProps) {
    this.client.track(name, { value, ...props });
  }

  identify(user: ShipperUser) {
    this.client.identify(user.id);
    // `userId` people property is numeric (matches the original Tracking impl so
    // existing Mixpanel People reports that filter it as a number keep working);
    // `id` itself stays a string for the distinct_id and Faro.
    this.client.people.set('userId', Number(user.id));
    this.client.people.set('email', user.email);
    this.client.people.set('phone_number', user.phoneNumber);
    this.client.people.set('is_worker', Boolean(user.isWorker));
  }

  setUserProperty(name: string, value: string | number | boolean) {
    this.client.people.set({ [name]: value });
  }

  setGroups(groups: ShipperGroup[]) {
    // Mixpanel group analytics key on a group type. Aggregate ids per type so a
    // type with several selected ids (e.g. multiple service departments) maps to
    // one `set_group(type, [...ids])` call, and label each group profile by name.
    const idsByType = groups.reduce<Record<string, string[]>>((acc, group) => {
      (acc[group.type] ??= []).push(group.id);
      return acc;
    }, {});
    Object.entries(idsByType).forEach(([type, ids]) => this.client.set_group(type, ids));
    groups.forEach((group) =>
      this.client.get_group(group.type, group.id).set({ $name: group.name ?? group.id, ...group.properties })
    );
  }

  register(extra: EventProps) {
    this.client.register(extra);
  }

  unregister(keys: string[]) {
    keys.forEach((key) => this.client.unregister(key));
  }

  // Manual session-replay control (independent of the auto `sampleRate`). Use to
  // force a recording on demand — e.g. the dev-settings smoke test. The SDK
  // lazy-loads the recorder bundle on first start.
  startSessionRecording() {
    this.client.start_session_recording();
  }

  stopSessionRecording() {
    this.client.stop_session_recording();
  }

  // `$mp_replay_id` of the active recording (empty object when none is running).
  // Explicit return type collapses Mixpanel's `{} | { $mp_replay_id?: string }`
  // union so the generated `.d.ts` doesn't emit a bare `{}` (banned by lint).
  getSessionRecordingProperties(): { $mp_replay_id?: string } {
    return this.client.get_session_recording_properties();
  }

  // The active recording: `$mp_replay_id` plus the SDK's deep link
  // (`mixpanel.com/projects/replay-redirect?…`, project resolved from the token).
  getReplay(): ShipperReplay | undefined {
    const sessionId = this.getSessionRecordingProperties().$mp_replay_id;
    return sessionId ? { sessionId, url: this.client.get_session_replay_url() ?? undefined } : undefined;
  }

  clear() {
    this.client.reset();
  }
}
